import { Link, useNavigate } from "react-router-dom";
import {
  LogOut,
  LayoutDashboard,
  ShieldCheck,
  Boxes,
  ClipboardList,
  Users,
  Lock,
  Handshake,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ps-medical-logistics/artifacts/1dt4h2l7_Gemini_Generated_Image_dpwsejdpwsejdpws.png";

export default function PortalDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  async function handleLogout() {
    await logout();
    navigate("/portal/login", { replace: true });
  }

  const isAdmin = user.role === "admin";

  return (
    <div className="min-h-screen bg-slate-50/60 flex flex-col">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="portal-logo-link">
            <img src={LOGO_URL} alt="PS GLOBAL SERIES, LLC" className="h-8 w-8 object-contain" />
            <div className="leading-tight">
              <div className="font-display text-[14px] font-bold tracking-tight text-slate-900">
                PS GLOBAL SERIES
              </div>
              <div className="eyebrow text-[9px] mt-0.5">Client Portal</div>
            </div>
          </Link>

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <div className="text-sm font-semibold text-slate-900" data-testid="portal-user-name">
                {user.name}
              </div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500">
                {user.role.replace("_", " ")}
              </div>
            </div>
            {isAdmin && (
              <Link
                to="/portal/admin"
                data-testid="portal-admin-link"
                className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-900 px-3 py-2 text-xs font-semibold tracking-tight rounded-sm"
              >
                <ShieldCheck size={14} /> Admin
              </Link>
            )}
            <button
              type="button"
              onClick={handleLogout}
              data-testid="portal-logout-btn"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 text-xs font-semibold tracking-tight rounded-sm"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 mx-auto max-w-7xl w-full px-6 lg:px-10 py-12">
        <div className="flex items-center gap-2 eyebrow text-emerald-700">
          <LayoutDashboard size={14} /> Dashboard
        </div>
        <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900">
          Welcome, <span className="font-semibold">{user.name.split(" ")[0]}</span>.
        </h1>
        <p className="mt-4 text-slate-600 max-w-2xl">
          This is your secured PS GLOBAL SERIES sourcing workspace. Modules
          below are scaffolded for your account — full functionality is
          rolled out in a phased deployment.
        </p>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <Module
            icon={Boxes}
            title="Supplier Shortlists"
            description="Browse curated Dominican Republic supplier shortlists, capability profiles, and reference checks scoped to your engagement."
            badge="Phase 1"
            testId="portal-module-suppliers"
          />
          <Module
            icon={Handshake}
            title="Negotiation Memos"
            description="Track active negotiation threads, pricing offers, MOQ proposals and production-term updates from each supplier."
            badge="Phase 1"
            testId="portal-module-negotiation"
          />
          <Module
            icon={ClipboardList}
            title="Procurement Pipeline"
            description="Milestone view of every sourcing engagement — from sourcing brief through PO issuance and supplier onboarding."
            badge="Phase 1"
            testId="portal-module-pipeline"
          />
          <Module
            icon={Users}
            title="Team Access"
            description="Manage authorized staff within your organization with role-based scopes."
            badge="Phase 2"
            testId="portal-module-team"
          />
          <Module
            icon={Lock}
            title="Audit Log"
            description="Timestamped log of every sourcing and trade-liaison action performed on behalf of your organization."
            badge="Phase 2"
            testId="portal-module-audit"
          />
        </div>
      </main>
    </div>
  );
}

function Module({ icon: Icon, title, description, badge, testId }) {
  return (
    <article
      data-testid={testId}
      className="group relative bg-white border border-slate-200 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-12px_rgba(15,23,42,0.15)] hover:border-slate-300"
    >
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 inline-flex items-center justify-center bg-slate-900 text-white rounded-sm">
          <Icon size={18} />
        </div>
        <div className="eyebrow text-[10px] text-[#B45309]">{badge}</div>
      </div>
      <h3 className="font-display mt-5 text-xl font-semibold tracking-tight text-slate-900">
        {title}
      </h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{description}</p>
      <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-slate-400">
        <Lock size={11} /> Coming online
      </div>
    </article>
  );
}
